import type { SvgIconComponent } from '@mui/icons-material';
import AssessmentOutlined from '@mui/icons-material/AssessmentOutlined';
import BatteryAlertOutlined from '@mui/icons-material/BatteryAlertOutlined';
import BlockOutlined from '@mui/icons-material/BlockOutlined';
import CalendarMonthOutlined from '@mui/icons-material/CalendarMonthOutlined';
import CategoryOutlined from '@mui/icons-material/CategoryOutlined';
import CheckroomOutlined from '@mui/icons-material/CheckroomOutlined';
import CompareArrowsOutlined from '@mui/icons-material/CompareArrowsOutlined';
import DashboardOutlined from '@mui/icons-material/DashboardOutlined';
import DeleteOutlineOutlined from '@mui/icons-material/DeleteOutlineOutlined';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import ErrorOutlineOutlined from '@mui/icons-material/ErrorOutlineOutlined';
import FactoryOutlined from '@mui/icons-material/FactoryOutlined';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import FitScreenOutlined from '@mui/icons-material/FitScreenOutlined';
import FlagOutlined from '@mui/icons-material/FlagOutlined';
import ForestOutlined from '@mui/icons-material/ForestOutlined';
import HardwareOutlined from '@mui/icons-material/HardwareOutlined';
import HubOutlined from '@mui/icons-material/HubOutlined';
import InsightsOutlined from '@mui/icons-material/InsightsOutlined';
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';
import KeyOffOutlined from '@mui/icons-material/KeyOffOutlined';
import LayersOutlined from '@mui/icons-material/LayersOutlined';
import LocalDrinkOutlined from '@mui/icons-material/LocalDrinkOutlined';
import LocalFireDepartmentOutlined from '@mui/icons-material/LocalFireDepartmentOutlined';
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined';
import LocationOnOutlined from '@mui/icons-material/LocationOnOutlined';
import LockOutlined from '@mui/icons-material/LockOutlined';
import MapOutlined from '@mui/icons-material/MapOutlined';
import MemoryOutlined from '@mui/icons-material/MemoryOutlined';
import MoreHorizOutlined from '@mui/icons-material/MoreHorizOutlined';
import NewspaperOutlined from '@mui/icons-material/NewspaperOutlined';
import OilBarrelOutlined from '@mui/icons-material/OilBarrelOutlined';
import OpacityOutlined from '@mui/icons-material/OpacityOutlined';
import PaymentsOutlined from '@mui/icons-material/PaymentsOutlined';
import PendingOutlined from '@mui/icons-material/PendingOutlined';
import PercentOutlined from '@mui/icons-material/PercentOutlined';
import PersonOutlineOutlined from '@mui/icons-material/PersonOutlineOutlined';
import PlaceOutlined from '@mui/icons-material/PlaceOutlined';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import RecyclingOutlined from '@mui/icons-material/RecyclingOutlined';
import RefreshOutlined from '@mui/icons-material/RefreshOutlined';
import RouteOutlined from '@mui/icons-material/RouteOutlined';
import ScaleOutlined from '@mui/icons-material/ScaleOutlined';
import ScheduleOutlined from '@mui/icons-material/ScheduleOutlined';
import ScienceOutlined from '@mui/icons-material/ScienceOutlined';
import ScreenRotationOutlined from '@mui/icons-material/ScreenRotationOutlined';
import SensorsOutlined from '@mui/icons-material/SensorsOutlined';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import SpaOutlined from '@mui/icons-material/SpaOutlined';
import StorefrontOutlined from '@mui/icons-material/StorefrontOutlined';
import StraightenOutlined from '@mui/icons-material/StraightenOutlined';
import TaskAltOutlined from '@mui/icons-material/TaskAltOutlined';
import TimelineOutlined from '@mui/icons-material/TimelineOutlined';
import TimerOutlined from '@mui/icons-material/TimerOutlined';
import TireRepairOutlined from '@mui/icons-material/TireRepairOutlined';
import TuneOutlined from '@mui/icons-material/TuneOutlined';
import UpdateOutlined from '@mui/icons-material/UpdateOutlined';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import WifiOffOutlined from '@mui/icons-material/WifiOffOutlined';
import WineBarOutlined from '@mui/icons-material/WineBarOutlined';

/**
 * One semantic icon per operational concept (Outlined family only). Icons
 * classify — they are monochrome, 16–20px, and never decorative; colour comes
 * from status tones, not from the icon.
 */
export const ZERO_ICONS = {
  // Operations
  dashboard: DashboardOutlined,
  collections: ReceiptLongOutlined,
  completed: TaskAltOutlined,
  inTransit: LocalShippingOutlined,
  pending: PendingOutlined,
  cancelled: BlockOutlined,
  rate: PercentOutlined,
  mass: ScaleOutlined,
  revenue: PaymentsOutlined,
  flow: HubOutlined,
  // Places and network
  branch: StorefrontOutlined,
  site: LocationOnOutlined,
  container: DeleteOutlineOutlined,
  facility: FactoryOutlined,
  stop: PlaceOutlined,
  map: MapOutlined,
  // Fleet
  vehicle: LocalShippingOutlined,
  route: RouteOutlined,
  driver: PersonOutlineOutlined,
  distance: StraightenOutlined,
  duration: TimerOutlined,
  clock: ScheduleOutlined,
  // Waste intelligence
  waste: RecyclingOutlined,
  materials: CategoryOutlined,
  daily: TimelineOutlined,
  targets: FlagOutlined,
  report: AssessmentOutlined,
  // Container alarms
  alarmFire: LocalFireDepartmentOutlined,
  alarmFull: DeleteOutlined,
  alarmTilt: ScreenRotationOutlined,
  alarmBattery: BatteryAlertOutlined,
  sensor: SensorsOutlined,
  attention: WarningAmberOutlined,
  // UI
  period: CalendarMonthOutlined,
  freshness: UpdateOutlined,
  refresh: RefreshOutlined,
  fitAll: FitScreenOutlined,
  customize: TuneOutlined,
  settings: SettingsOutlined,
  compare: CompareArrowsOutlined,
  download: FileDownloadOutlined,
  insights: InsightsOutlined,
  locked: LockOutlined,
  error: ErrorOutlineOutlined,
  offline: WifiOffOutlined,
  noKey: KeyOffOutlined,
} satisfies Record<string, SvgIconComponent>;

export type ZeroIconKey = keyof typeof ZERO_ICONS;

/**
 * Material categories: the 14 catalog `parent_category` codes plus the legacy
 * dashboard waste types (`paper`, `other`). The dashboard API still folds wood,
 * sludge, mixed, chemical, e-waste, rubber and textiles into `other`.
 */
export const MATERIAL_ICONS = {
  organic: SpaOutlined,
  cardboard: Inventory2Outlined,
  paper: NewspaperOutlined,
  plastic: LocalDrinkOutlined,
  metal: HardwareOutlined,
  wood: ForestOutlined,
  glass: WineBarOutlined,
  oil: OilBarrelOutlined,
  sludge: OpacityOutlined,
  mixed: LayersOutlined,
  chemical: ScienceOutlined,
  e_waste: MemoryOutlined,
  rubber: TireRepairOutlined,
  textiles: CheckroomOutlined,
  other: MoreHorizOutlined,
} satisfies Record<string, SvgIconComponent>;

export type MaterialKey = keyof typeof MATERIAL_ICONS;

const MATERIAL_ALIASES: Record<string, MaterialKey> = {
  ewaste: 'e_waste',
  electronic: 'e_waste',
  electronics: 'e_waste',
  electronic_waste: 'e_waste',
  textile: 'textiles',
  plastics: 'plastic',
  metals: 'metal',
  cardboard_and_paper: 'cardboard',
  organics: 'organic',
  mixed_waste: 'mixed',
  chemical_waste: 'chemical',
};

/** `"E-Waste"`, `"e_waste"`, `"Plastic"` → canonical material key; unknown → `other`. */
export function normalizeMaterialKey(raw: string | null | undefined): MaterialKey {
  if (!raw) return 'other';
  const key = raw
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  if (key in MATERIAL_ICONS) return key as MaterialKey;
  return MATERIAL_ALIASES[key] ?? MATERIAL_ALIASES[key.replace(/_/g, '')] ?? 'other';
}

export function materialIcon(raw: string | null | undefined): SvgIconComponent {
  return MATERIAL_ICONS[normalizeMaterialKey(raw)];
}
